<script setup>
import { ref, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import InboxesAPI from 'dashboard/api/inboxes';
import { useLiveRefresh } from 'dashboard/composables/useLiveRefresh';

const { t } = useI18n();
const router = useRouter();
const store = useStore();

// State
const inboxName = ref('');
const createdInbox = ref(null);
const showQrScreen = ref(false);
const isCreating = ref(false);
const connectionStatus = ref('idle');
const connectedPhone = ref('');
const pollCount = ref(0);
const MAX_POLL_COUNT = 60;
const hasAlerted = ref(false);

const checkStatus = async () => {
  try {
    pollCount.value += 1;

    if (pollCount.value >= MAX_POLL_COUNT) {
      connectionStatus.value = 'timeout';
      stopRefetching();
      return;
    }

    const inboxId = createdInbox.value?.id;
    if (!inboxId) return;

    // Cache-buster avoids stale 304 responses during rapid polling.
    const response = await InboxesAPI.show(`${inboxId}?_t=${Date.now()}`);
    const providerConfig = response?.data?.provider_config || {};
    const latestStatus = providerConfig.status;
    const latestPhone =
      providerConfig.phone_number || response?.data?.phone_number || '';

    createdInbox.value = {
      ...createdInbox.value,
      status: latestStatus || createdInbox.value?.status,
      phone_number: latestPhone || createdInbox.value?.phone_number,
    };

    const isAuthorized =
      providerConfig.status === 'authorized' ||
      providerConfig.authenticated === true;

    if (isAuthorized && !hasAlerted.value) {
      connectionStatus.value = 'connected';
      connectedPhone.value = latestPhone;
      hasAlerted.value = true;
      stopRefetching();

      useAlert(
        connectedPhone.value
          ? `WhatsApp เชื่อมต่อสำเร็จ! เบอร์: ${connectedPhone.value}`
          : 'WhatsApp เชื่อมต่อสำเร็จ!'
      );
    }
  } catch (error) {
    console.error('[GreenAPI] Status poll error:', error);
  }
};

const { startRefetching, stopRefetching } = useLiveRefresh(checkStatus, 3000);

watch(showQrScreen, isShown => {
  if (!isShown) {
    stopRefetching();
  }
});

// Validation
const rules = {
  inboxName: { required },
};

const v$ = useVuelidate(rules, { inboxName });

// Computed
const uiFlags = computed(() => store.getters['inboxes/getUIFlags']);

const hasQrCodeUrl = computed(() => {
  return createdInbox.value?.qr_code_url;
});

// Check if qr_code is Base64 PNG (starts with PNG signature)
const isBase64PngQrCode = computed(() => {
  const qrCode = createdInbox.value?.qr_code;
  // PNG Base64 starts with "iVBORw0K"
  return qrCode && typeof qrCode === 'string' && qrCode.startsWith('iVBORw0K');
});

// Build data URL for Base64 PNG display
const qrCodeDataUrl = computed(() => {
  if (isBase64PngQrCode.value) {
    return `data:image/png;base64,${createdInbox.value.qr_code}`;
  }
  return null;
});

// Methods
const createChannel = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  try {
    isCreating.value = true;

    const response = await store.dispatch('inboxes/createChannel', {
      name: inboxName.value,
      channel: {
        type: 'whatsapp',
        provider: 'green_api',
      },
    });

    // Green API response format: { id, name, qr_code, qr_code_url, instance_id, status }
    createdInbox.value = response;
    showQrScreen.value = true;
    connectionStatus.value = 'waiting';
    pollCount.value = 0;
    hasAlerted.value = false;
    startRefetching();
    await checkStatus();
  } catch (error) {
    const errorMessage =
      error.message || t('INBOX_MGMT.ADD.WHATSAPP.GREEN_API.ERROR_MESSAGE');
    useAlert(errorMessage);
  } finally {
    isCreating.value = false;
  }
};

const goToInboxSettings = () => {
  stopRefetching();
  router.push({
    name: 'settings_inboxes_add_agents',
    params: {
      page: 'new',
      inbox_id: createdInbox.value.id,
    },
  });
};

const goBack = () => {
  stopRefetching();
  connectionStatus.value = 'idle';
  showQrScreen.value = false;
  createdInbox.value = null;
  inboxName.value = '';
  v$.value.$reset();
};

const retryPolling = () => {
  connectionStatus.value = 'waiting';
  pollCount.value = 0;
  hasAlerted.value = false;
  startRefetching();
};
</script>

<template>
  <div class="overflow-auto col-span-6 p-6 w-full h-full">
    <!-- Form Screen -->
    <form
      v-if="!showQrScreen"
      class="flex flex-col gap-6"
      @submit.prevent="createChannel"
    >
      <div class="mb-4">
        <h1 class="mb-2 text-xl font-medium text-slate-12">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.GREEN_API.TITLE') }}
        </h1>
        <p class="text-sm leading-relaxed text-slate-11">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.GREEN_API.SUBTITLE') }}
        </p>
      </div>

      <!-- Info Message (hidden when empty) -->
      <div
        v-if="$t('INBOX_MGMT.ADD.WHATSAPP.GREEN_API.INFO_MESSAGE')"
        class="p-4 rounded-lg bg-blue-50 border border-blue-200"
      >
        <div class="flex items-start gap-3">
          <span class="text-blue-600">
            <svg
              class="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </span>
          <p class="text-sm text-blue-900">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.GREEN_API.INFO_MESSAGE') }}
          </p>
        </div>
      </div>

      <!-- Inbox Name Input -->
      <div class="flex flex-col gap-2">
        <label
          for="green-api-inbox-name"
          class="text-sm font-medium text-slate-12"
          :class="{ 'text-red-600': v$.inboxName.$error }"
        >
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.GREEN_API.INBOX_NAME.LABEL') }}
        </label>
        <input
          id="green-api-inbox-name"
          v-model="inboxName"
          type="text"
          class="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          :class="{
            'border-slate-300': !v$.inboxName.$error,
            'border-red-500': v$.inboxName.$error,
          }"
          :placeholder="
            $t('INBOX_MGMT.ADD.WHATSAPP.GREEN_API.INBOX_NAME.PLACEHOLDER')
          "
          @blur="v$.inboxName.$touch"
        />
        <span v-if="v$.inboxName.$error" class="text-sm text-red-600">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.GREEN_API.INBOX_NAME.ERROR') }}
        </span>
      </div>

      <!-- Submit Button -->
      <div class="flex gap-3 mt-4">
        <NextButton
          type="submit"
          :is-loading="isCreating || uiFlags.isCreating"
          :label="$t('INBOX_MGMT.ADD.WHATSAPP.GREEN_API.CREATE_BUTTON')"
          solid
          blue
        />
      </div>
    </form>

    <!-- QR Code Screen -->
    <div v-else class="flex flex-col gap-6">
      <!-- Success Message -->
      <div class="mb-4">
        <h1 class="mb-2 text-xl font-medium text-slate-12">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.GREEN_API.QR_SECTION.TITLE') }}
        </h1>
        <p class="text-sm leading-relaxed text-green-600">
          {{
            $t('INBOX_MGMT.ADD.WHATSAPP.GREEN_API.QR_SECTION.SUCCESS_MESSAGE')
          }}
        </p>
      </div>

      <!-- Connection Status: Connected -->
      <div
        v-if="connectionStatus === 'connected'"
        class="p-4 rounded-lg bg-green-50 border-2 border-green-400 flex items-center gap-3 shadow-sm"
      >
        <svg
          class="w-8 h-8 text-green-600 flex-shrink-0"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <div>
          <p class="text-lg font-bold text-green-800">
            WhatsApp เชื่อมต่อสำเร็จแล้ว!
          </p>
          <p v-if="connectedPhone" class="text-sm text-green-700 mt-1">
            เบอร์ที่เชื่อมต่อ: <strong>{{ connectedPhone }}</strong>
          </p>
          <p class="text-sm text-green-600 mt-1">
            กรุณากด "ถัดไป" เพื่อตั้งค่า Agent
          </p>
        </div>
      </div>

      <!-- Connection Status: Waiting -->
      <div
        v-else-if="connectionStatus === 'waiting'"
        class="p-3 rounded-lg bg-yellow-50 border border-yellow-200 flex items-center gap-3"
      >
        <div
          class="animate-spin w-5 h-5 border-2 border-yellow-600 border-t-transparent rounded-full flex-shrink-0"
        />
        <p class="text-sm text-yellow-800">
          กำลังรอการ Scan QR Code... (ตรวจสอบอัตโนมัติทุก 3 วินาที)
        </p>
      </div>

      <!-- Connection Status: Timeout -->
      <div
        v-else-if="connectionStatus === 'timeout'"
        class="p-3 rounded-lg bg-red-50 border border-red-200 flex items-center gap-3"
      >
        <svg
          class="w-5 h-5 text-red-500 flex-shrink-0"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <div class="flex-1">
          <p class="text-sm text-red-800">หมดเวลารอการ Scan กรุณาลองใหม่</p>
        </div>
        <button
          class="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200"
          @click="retryPolling"
        >
          ลองใหม่
        </button>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <!-- Left Column: QR Code -->
        <div class="flex flex-col gap-4">
          <div class="p-6 bg-white rounded-lg border border-slate-200">
            <h3 class="mb-4 text-sm font-medium text-slate-12">
              {{
                $t('INBOX_MGMT.ADD.WHATSAPP.GREEN_API.QR_SECTION.QR_CODE_LABEL')
              }}
            </h3>

            <!-- QR Code Image (if qr_code_url available) -->
            <div
              v-if="hasQrCodeUrl"
              class="flex justify-center p-4 bg-white rounded"
            >
              <img
                :src="createdInbox.qr_code_url"
                alt="WhatsApp QR Code"
                class="max-w-full h-auto"
                style="max-width: 300px"
              />
            </div>

            <!-- QR Code Image (if qr_code is Base64 PNG) -->
            <div
              v-else-if="isBase64PngQrCode"
              class="flex justify-center p-4 bg-white rounded"
            >
              <img
                :src="qrCodeDataUrl"
                alt="WhatsApp QR Code"
                class="max-w-full h-auto"
                style="max-width: 300px"
              />
            </div>

            <!-- QR Code Text (fallback for non-image data) -->
            <div
              v-else
              class="p-4 bg-slate-50 rounded font-mono text-xs break-all"
            >
              {{ createdInbox.qr_code }}
            </div>
          </div>

          <!-- Instance Info -->
          <div class="p-4 bg-slate-50 rounded-lg space-y-2 text-sm">
            <div>
              <span class="font-medium text-slate-11">
                {{
                  $t(
                    'INBOX_MGMT.ADD.WHATSAPP.GREEN_API.QR_SECTION.INSTANCE_ID_LABEL'
                  )
                }}
              </span>
              <span class="ml-2 text-slate-12 font-mono">
                {{ createdInbox.instance_id }}
              </span>
            </div>
            <div>
              <span class="font-medium text-slate-11">
                {{
                  $t(
                    'INBOX_MGMT.ADD.WHATSAPP.GREEN_API.QR_SECTION.STATUS_LABEL'
                  )
                }}
              </span>
              <span class="ml-2 text-slate-12">
                {{ createdInbox.status || 'created' }}
              </span>
            </div>
          </div>
        </div>

        <!-- Right Column: Instructions -->
        <div class="flex flex-col gap-4">
          <div class="p-6 bg-blue-50 rounded-lg border border-blue-200">
            <h3 class="mb-4 text-sm font-semibold text-blue-900">
              {{
                $t(
                  'INBOX_MGMT.ADD.WHATSAPP.GREEN_API.QR_SECTION.INSTRUCTIONS.TITLE'
                )
              }}
            </h3>
            <ol class="space-y-3 text-sm text-blue-800">
              <li class="flex items-start gap-2">
                <span
                  class="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold"
                  >1</span
                >
                <span>{{
                  $t(
                    'INBOX_MGMT.ADD.WHATSAPP.GREEN_API.QR_SECTION.INSTRUCTIONS.STEP_1'
                  )
                }}</span>
              </li>
              <li class="flex items-start gap-2">
                <span
                  class="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold"
                  >2</span
                >
                <span>{{
                  $t(
                    'INBOX_MGMT.ADD.WHATSAPP.GREEN_API.QR_SECTION.INSTRUCTIONS.STEP_2'
                  )
                }}</span>
              </li>
              <li class="flex items-start gap-2">
                <span
                  class="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold"
                  >3</span
                >
                <span>{{
                  $t(
                    'INBOX_MGMT.ADD.WHATSAPP.GREEN_API.QR_SECTION.INSTRUCTIONS.STEP_3'
                  )
                }}</span>
              </li>
              <li class="flex items-start gap-2">
                <span
                  class="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold"
                  >4</span
                >
                <span>{{
                  $t(
                    'INBOX_MGMT.ADD.WHATSAPP.GREEN_API.QR_SECTION.INSTRUCTIONS.STEP_4'
                  )
                }}</span>
              </li>
            </ol>
          </div>

          <div class="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
            <p class="text-sm text-yellow-900">
              <strong>Note:</strong> After scanning, try sending a message from
              your phone to test the connection.
            </p>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="flex gap-3 mt-6 pt-6 border-t border-slate-200">
        <NextButton
          :label="$t('INBOX_MGMT.ADD.WHATSAPP.GREEN_API.FINISH_BUTTON')"
          solid
          blue
          @click="goToInboxSettings"
        />
        <NextButton
          :label="$t('INBOX_MGMT.ADD.WHATSAPP.GREEN_API.CANCEL_BUTTON')"
          @click="goBack"
        />
      </div>
    </div>
  </div>
</template>
